/**
 * Crosshair pick mode for tagging canvas elements as agent chat context.
 *
 * Modeled on format-painter.js, with one deliberate difference: picking does
 * NOT exit the mode. Tagging several places in a row is the common case, so
 * the mode ends only on Esc, the composer toggle, or a context change
 * (preview toggle, project switch).
 */

import { eventBus } from '../core/events.js';

let _active = false;
let _escListener = null;

export function enterPickMode() {
  if (_active) return false;
  _active = true;
  _attachEscListener();
  _setBodyPickCursor(true);
  eventBus.emit('ai-picker:entered');
  return true;
}

export function exitPickMode() {
  if (!_active) return false;
  _active = false;
  _detachEscListener();
  _setBodyPickCursor(false);
  eventBus.emit('ai-picker:exited');
  return true;
}

export function isPickModeActive() {
  return _active;
}

function _attachEscListener() {
  if (_escListener || typeof window === 'undefined') return;
  _escListener = (e) => {
    if (!_active) return;
    const key = (e.key || '').toLowerCase();
    if (key !== 'escape' && key !== 'esc') return;
    // Capture phase beats the agent drawer's own Escape handler, so Esc leaves
    // pick mode without also closing the panel the user is composing in.
    e.preventDefault();
    e.stopPropagation();
    exitPickMode();
  };
  window.addEventListener('keydown', _escListener, true);
}

function _detachEscListener() {
  if (!_escListener || typeof window === 'undefined') return;
  window.removeEventListener('keydown', _escListener, true);
  _escListener = null;
}

function _setBodyPickCursor(on) {
  if (typeof document === 'undefined') return;
  const body = document.body;
  if (!body) return;
  if (on) body.classList.add('pb-ai-pick-mode');
  else body.classList.remove('pb-ai-pick-mode');
}

/** Test-only reset. */
export function _resetForTests() {
  _active = false;
  _detachEscListener();
  _setBodyPickCursor(false);
}
